/**
 * Kronstadt – Product Slider
 *
 * Card widths are calculated in JS (ResizeObserver) so layout
 * is always pixel-perfect regardless of container/padding setup.
 *
 * Drag uses Pointer Events + setPointerCapture:
 *   – pointer is locked to the element even outside its bounds
 *   – mouse only: touch uses native overflow-x scroll (smoother on mobile)
 *
 * Release fling uses a short inertia phase without a forced snap.
 * This avoids the jumpy end-state that happens when inertia and
 * snapping both try to settle the track.
 */

const DEFAULT_PEEK_PX = 60;

class KronSliderSection {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.outerEl   = document.getElementById(sectionId);
    this.contentEl = document.getElementById(sectionId + '-content');
    if (!this.outerEl) return;

    this.prevBtns = [...this.outerEl.querySelectorAll('.kron-slider-arrow--prev')];
    this.nextBtns = [...this.outerEl.querySelectorAll('.kron-slider-arrow--next')];

    this.#initArrows();
    this.#initTracks();
    this.#observeTabSwitch();
    this.#updateArrows();
  }

  /* ── Active track ─────────────────────────────────────── */
  get activeTrack() {
    if (!this.contentEl) return null;
    const sel = this.contentEl.querySelector('.hdt-tab-item[tab-selected]');
    return (sel ?? this.contentEl).querySelector('.kron-product-slider__track');
  }

  /* ── Arrows ───────────────────────────────────────────── */
  #initArrows() {
    this.prevBtns.forEach(btn => btn.addEventListener('click', () => this.#slide(-1)));
    this.nextBtns.forEach(btn => btn.addEventListener('click', () => this.#slide(1)));
  }

  #slide(dir) {
    const track = this.activeTrack;
    if (!track) return;
    const item = track.querySelector('.kron-product-slider__item');
    if (!item) return;
    const gap = parseFloat(getComputedStyle(track).gap) || 0;
    const step = item.offsetWidth + gap;
    const maxScroll = track.scrollWidth - track.clientWidth;
    const currentStep = track.scrollLeft / step;
    const epsilon = 0.01;
    const targetIndex = dir > 0
      ? Math.floor(currentStep + epsilon) + 1
      : Math.ceil(currentStep - epsilon) - 1;
    const targetLeft = Math.max(0, Math.min(maxScroll, targetIndex * step));
    track.scrollTo({ left: targetLeft, behavior: 'smooth' });
    setTimeout(() => this.#updateArrows(), 450);
  }

  #updateArrows() {
    if (!this.prevBtns.length || !this.nextBtns.length) return;
    const track = this.activeTrack;
    if (!track) return;
    const atStart = track.scrollLeft <= 1;
    const atEnd   = track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;
    this.prevBtns.forEach(btn => { btn.disabled = atStart; });
    this.nextBtns.forEach(btn => { btn.disabled = atEnd; });
  }

  /* ── Tracks ───────────────────────────────────────────── */
  #initTracks() {
    const ro = new ResizeObserver(() => this.#setCardWidths());

    this.outerEl.querySelectorAll('.kron-product-slider__track').forEach(track => {
      new KronTrackDrag(track, () => this.#updateArrows());
      track.addEventListener('scroll', () => this.#updateArrows(), { passive: true });
      ro.observe(track);
    });
  }

  /* Card widths in px – always accurate, responsive-aware */
  #setCardWidths() {
    this.outerEl.querySelectorAll('.kron-product-slider__track').forEach(track => {
      const sliderEl = track.closest('.kron-product-slider');
      const styles = getComputedStyle(sliderEl ?? track);
      const gap  = parseFloat(styles.getPropertyValue('--kron-slider-gap-current')) || parseFloat(styles.getPropertyValue('--kron-slider-gap')) || 20;
      const peek = parseFloat(styles.getPropertyValue('--kron-slider-peek-current')) || parseFloat(styles.getPropertyValue('--kron-slider-peek')) || DEFAULT_PEEK_PX;
      const cols = this.#currentCols(track);
      const w    = track.clientWidth;
      if (!w) return;

      const cardW = Math.floor((w - cols * gap - peek) / cols);
      track.querySelectorAll('.kron-product-slider__item').forEach(item => {
        item.style.width = Math.max(80, cardW) + 'px';
      });
    });
  }

  #currentCols(track) {
    const vw = window.innerWidth;
    if (vw >= 1150) return parseInt(track.dataset.colsDk) || 4;
    if (vw >= 768)  return parseInt(track.dataset.colsTb) || 2;
    return 1;
  }

  /* ── Tab switching ────────────────────────────────────── */
  #observeTabSwitch() {
    if (!this.contentEl) return;
    new MutationObserver(() => {
      const track = this.activeTrack;
      if (track) track.scrollLeft = 0;
      this.#setCardWidths();
      this.#updateArrows();
    }).observe(this.contentEl, { subtree: true, attributes: true, attributeFilter: ['tab-selected'] });
  }
}

/* ============================================================
   KronTrackDrag
   Pointer Events + setPointerCapture for reliable mouse drag.
   On release: inertia runs freely with no forced snap at the end.
   This keeps the finish softer and removes the jumpy settle.
   ============================================================ */
class KronTrackDrag {
  #track;
  #onScrollCb;

  #active      = false;
  #hasDragged  = false;
  #startX      = 0;
  #scrollStart = 0;
  #velocity    = 0;   // px/ms (positive = scrolling right)
  #lastX       = 0;
  #lastTime    = 0;
  #rafId       = 0;
  #lastFrameTs = 0;

  constructor(track, onScrollCallback) {
    this.#track      = track;
    this.#onScrollCb = onScrollCallback;
    this.#bind();
  }

  #bind() {
    const t = this.#track;
    t.addEventListener('pointerdown',   e => this.#down(e));
    t.addEventListener('pointermove',   e => this.#move(e), { passive: true });
    t.addEventListener('pointerup',     e => this.#up(e));
    t.addEventListener('pointercancel', e => this.#up(e));
    t.addEventListener('click',         e => this.#click(e), true);
    t.addEventListener('wheel',         e => this.#wheel(e), { passive: false });
    t.addEventListener('dragstart',     e => e.preventDefault());
  }

  #down(e) {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;

    this.#stopMomentum();
    this.#track.style.scrollBehavior = 'auto';

    // Do NOT call setPointerCapture here – that would redirect the
    // subsequent click event away from the actual link/button target.
    // Capture is set lazily in #move once a real drag is confirmed.

    this.#active      = true;
    this.#hasDragged  = false;
    this.#startX      = e.clientX;
    this.#scrollStart = this.#track.scrollLeft;
    this.#velocity    = 0;
    this.#lastX       = e.clientX;
    this.#lastTime    = performance.now();
    this.#track.classList.add('is-dragging');
  }

  #move(e) {
    if (!this.#active) return;

    const dx = e.clientX - this.#startX;
    if (Math.abs(dx) > 4 && !this.#hasDragged) {
      this.#hasDragged = true;
      // Capture the pointer now so drag continues even outside the track
      this.#track.setPointerCapture(e.pointerId);
    }

    // 1:1 mapping of pointer movement to scroll position
    this.#track.scrollLeft = this.#scrollStart - dx;

    const now = performance.now();
    const dt  = now - this.#lastTime;
    if (dt > 0) {
      // Exponential moving average for stable velocity
      const raw = (this.#lastX - e.clientX) / dt;
      this.#velocity = this.#velocity * 0.6 + raw * 0.4;
      this.#lastX    = e.clientX;
      this.#lastTime = now;
    }
  }

  #up(e) {
    if (!this.#active) return;
    this.#active = false;
    this.#track.classList.remove('is-dragging');

    if (this.#track.hasPointerCapture(e.pointerId)) {
      this.#track.releasePointerCapture(e.pointerId);
    }

    this.#track.style.scrollBehavior = '';
    if (this.#hasDragged) {
      this.#startMomentum();
    }

    this.#onScrollCb?.();
  }

  /* Block link/card navigation if pointer actually moved */
  #click(e) {
    if (this.#hasDragged) {
      e.preventDefault();
      e.stopPropagation();
      this.#hasDragged = false;
    }
  }

  /* Allow normal page scroll, but block horizontal wheel/trackpad input.
     Require deltaX to be clearly dominant (2× deltaY) AND above a minimum
     threshold to avoid false positives from slight diagonal trackpad input. */
  #wheel(e) {
    const deltaX = Math.abs(e.deltaX);
    const deltaY = Math.abs(e.deltaY);
    const isHorizontalIntent = e.shiftKey || (deltaX > deltaY * 2 && deltaX > 3);

    if (isHorizontalIntent) {
      e.preventDefault();
    }
  }

  #startMomentum() {
    if (Math.abs(this.#velocity) < 0.02) {
      this.#stopMomentum();
      this.#onScrollCb?.();
      return;
    }

    this.#track.classList.add('is-settling');
    this.#lastFrameTs = performance.now();

    const step = now => {
      const dt = Math.max(8, now - this.#lastFrameTs);
      this.#lastFrameTs = now;

      const maxScroll = this.#track.scrollWidth - this.#track.clientWidth;
      const next = this.#track.scrollLeft + this.#velocity * dt;
      const clamped = Math.max(0, Math.min(maxScroll, next));

      this.#track.scrollLeft = clamped;
      this.#velocity *= Math.pow(0.94, dt / 16.67);
      this.#onScrollCb?.();

      const hitEdge = clamped <= 0 || clamped >= maxScroll;
      if (Math.abs(this.#velocity) < 0.02 || hitEdge) {
        this.#stopMomentum();
        return;
      }

      this.#rafId = requestAnimationFrame(step);
    };

    this.#rafId = requestAnimationFrame(step);
  }

  #stopMomentum() {
    if (this.#rafId) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = 0;
    }
    this.#velocity = 0;
  }
}

/* ============================================================
   Init
   ============================================================ */
function initKronProductSliders() {
  const seen = new Set();
  document.querySelectorAll('.kron-product-slider[data-section-id]').forEach(el => {
    const id = el.dataset.sectionId;
    if (!seen.has(id)) {
      seen.add(id);
      new KronSliderSection(id);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initKronProductSliders);
} else {
  initKronProductSliders();
}
